import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import moment from 'moment';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const attestationCache = new Map();

const formatDate = date => (date ? moment(date).format('MM/DD/YY hh:mm A') : '-');

const CycleAttestation = ({ cycleid }) => {
    const [attestation, setAttestation] = useState(null);
    const [selectedPatientId, setSelectedPatientId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeFilter, setActiveFilter] = useState('ALL');

    useEffect(() => {
        const fetchAttestation = async () => {
            if (!cycleid) return;

            if (attestationCache.has(cycleid)) {
                setAttestation(attestationCache.get(cycleid));
                setSelectedPatientId('');
                setError('');
                return;
            }

            setLoading(true);
            setError('');

            try {
                const tokenStr = localStorage.getItem('accessToken');
                const { data } = await axios.get(
                    `${process.env.REACT_APP_SERVER_URL}/cycles/${cycleid}/attestation`,
                    { headers: { Authorization: `Bearer ${tokenStr}` } }
                );

                if (data?.status) {
                    attestationCache.set(cycleid, data.data);
                    setAttestation(data.data);
                    setSelectedPatientId('');
                } else {
                    setAttestation(null);
                    setError(data?.message || 'Unable to load attestation details');
                }
            } catch {
                setAttestation(null);
                setError('Unable to load attestation details');
            } finally {
                setLoading(false);
            }
        };

        fetchAttestation();
    }, [cycleid]);

    const patients = useMemo(
        () => (Array.isArray(attestation?.patients) ? attestation.patients : []),
        [attestation]
    );

    const filteredPatients = useMemo(() => {
        if (!selectedPatientId) return patients;

        return patients.filter(
            patient => String(patient.patient_id) === String(selectedPatientId)
        );
    }, [patients, selectedPatientId]);

    const rows = useMemo(() => {
        let resultRows = filteredPatients.flatMap(patient => {
            const patientName = `${patient.patient_firstname || ''} ${patient.patient_lastname || ''}`.trim();

            return (patient.prescriptions || []).map(prescription => ({
                id: prescription.cycle_detail_id,
                patient: patientName || '-',
                externalPatientId: patient.external_patient_id || '-',
                patientStatus: patient.is_attested,
                patientAttestedBy: patient.attested_by || '-',
                patientAttestedOn: formatDate(patient.attested_on),
                rx: prescription.external_prescription_id || '-',
                drug: prescription.drug || '-',
                rxAttested: prescription.rx_attested,
                rxAttestedBy: prescription.rx_attested_by || '-',
                rxAttestedOn: formatDate(prescription.rx_attested_on),
                hcpConfirmed: prescription.hcp_confirmed,
                hcpConfirmedBy: prescription.hcp_confirmed_by || '-',
                hcpConfirmedOn: formatDate(prescription.hcp_confirmed_on)
            }));
        });

        if (activeFilter === 'ATTESTED_PATIENTS') {
            resultRows = resultRows.filter(row => row.patientStatus === true);
        } else if (activeFilter === 'PENDING_PATIENTS') {
            resultRows = resultRows.filter(row => row.patientStatus !== true);
        } else if (activeFilter === 'PENDING_RX') {
            resultRows = resultRows.filter(row => row.rxAttested !== true);
        } else if (activeFilter === 'PENDING_HCP') {
            resultRows = resultRows.filter(row => row.hcpConfirmed !== true);
        }

        return resultRows;
    }, [filteredPatients, activeFilter]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'patient',
                header: 'Patient',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'externalPatientId',
                header: 'Patient ID',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'rx',
                header: 'Rx',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'drug',
                header: 'Drug',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'rxAttested',
                header: 'Rx Attested',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                // cell: ({ row: { original } }) => <StatusBadge value={original.rxAttested} />
                cell: ({ row: { original } }) => original.rxAttested ? <FontAwesomeIcon
                    icon="check"
                    className="text-success"
                /> : '-'
            },
            {
                accessorKey: 'rxAttestedBy',
                header: 'Rx By',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'rxAttestedOn',
                header: 'Rx On',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'hcpConfirmed',
                header: 'HCP Confirmed',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                // cell: ({ row: { original } }) => <StatusBadge value={original.hcpConfirmed} />
                cell: ({ row: { original } }) => original.hcpConfirmed ? <FontAwesomeIcon
                    icon="check"
                    className="text-success"
                /> : '-'
            },
            {
                accessorKey: 'hcpConfirmedBy',
                header: 'HCP By',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'hcpConfirmedOn',
                header: 'HCP On',
                meta: {
                    headerProps: { className: 'text-900 text-start px-2' },
                    cellProps: { className: 'text-start px-2' }
                }
            },
            {
                accessorKey: 'patientStatus',
                header: 'Patient',
                meta: {
                    headerProps: { className: 'text-900 text-center px-2' },
                    cellProps: { className: 'text-center px-2' }
                },
                // cell: ({ row: { original } }) => <StatusBadge value={original.patientStatus} />
                cell: ({ row: { original } }) => original.patientStatus ? <FontAwesomeIcon
                    icon="check"
                    className="text-success"
                /> : '-'
            }
        ],
        []
    );

    const table = useAdvanceTable({
        data: rows,
        columns,
        selection: false,
        sortable: true,
        pagination: true
    });

    const summary = attestation?.summary || {};

    return (
        <div className="mt-3">
            {loading && (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="fs-10">Loading attestation details</span>
                </div>
            )}

            {!loading && error && (
                <div className="text-danger small py-2">{error}</div>
            )}

            {!loading && !error && attestation && (
                <>
                    <Row className="g-3 mb-3">
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ALL' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ALL')}
                            >
                                <div className="text-700 small">Total Patients</div>
                                <div className="fw-bold fs-6">{summary.total_patients || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ATTESTED_PATIENTS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ATTESTED_PATIENTS')}
                            >
                                <div className="text-700 small">Attested Patients</div>
                                <div className="fw-bold fs-6">{summary.fully_attested_patients || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_PATIENTS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_PATIENTS')}
                            >
                                <div className="text-700 small">Attestation Pending Patients</div>
                                <div className="fw-bold fs-6">{summary.pending_patients || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'ALL_PRESCRIPTIONS' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('ALL_PRESCRIPTIONS')}
                            >
                                <div className="text-700 small">Total Prescriptions</div>
                                <div className="fw-bold fs-6">{summary.total_prescriptions || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_RX' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_RX')}
                            >
                                <div className="text-700 small">Medication Accuracy Check Pending</div>
                                <div className="fw-bold fs-6">{summary.pending_rx_attestation || 0}</div>
                            </div>
                        </Col>
                        <Col md={2} sm={4} xs={6}>
                            <div 
                                className={`border rounded-2 p-2 h-100 ${activeFilter === 'PENDING_HCP' ? 'border-primary bg-100 shadow-sm' : ''}`}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setActiveFilter('PENDING_HCP')}
                            >
                                <div className="text-700 small">HCP Confirmation Pending</div>
                                <div className="fw-bold fs-6">{summary.pending_hcp_confirmation || 0}</div>
                            </div>
                        </Col>
                    </Row>

                    <AdvanceTableProvider {...table}>
                        <Row className="align-items-end justify-content-between mb-3">
                            <Col md={4} className="mb-2 mb-md-0">
                                <AdvanceTableSearchBox placeholder="Search..." />
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label className="mb-1">Patient</Form.Label>
                                    <Form.Select
                                        size="sm"
                                        value={selectedPatientId}
                                        onChange={event => setSelectedPatientId(event.target.value)}
                                    >
                                        <option value="">All Patients</option>
                                        {patients.map(patient => {
                                            const name = `${patient.patient_firstname || ''} ${patient.patient_lastname || ''}`.trim();

                                            return (
                                                <option key={patient.patient_id} value={patient.patient_id}>
                                                    {name || patient.external_patient_id || patient.patient_id}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        {rows.length > 0 ? (
                            <>
                                <Row>
                                    <Col xs="12" className="mb-1 mt-1">
                                        <AdvanceTable
                                            headerClassName="bg-200 text-nowrap align-middle text-center"
                                            rowClassName="text-nowrap align-middle text-center"
                                            tableProps={{
                                                size: 'sm',
                                                striped: true,
                                                className: 'fs-10 mb-0 overflow-hidden'
                                            }}
                                        />
                                    </Col>
                                </Row>
                                <Card.Footer>
                                    <div className="mt-3">
                                        <AdvanceTableFooter
                                            rowInfo
                                            navButtons
                                            rowsPerPageSelection
                                        />
                                    </div>
                                </Card.Footer>
                            </>
                        ) : (
                            <div className="text-700 small py-3">No attestation records found</div>
                        )}
                    </AdvanceTableProvider>
                </>
            )}
        </div>
    );
};

export default CycleAttestation;
